const allJobStatuses = new Map<string, GeckoJobStatus>()

/**
 * A representation of the publishing state of the data: either
 * Raw, Working, or Published
 */
export class GeckoJobStatus {
  //----------------------------------------------------------------------------

  public static readonly Scheduled = new GeckoJobStatus(
    'scheduled',
    'Scheduled',
    'The job is scheduled'
  )

  public static readonly InProgress = new GeckoJobStatus(
    'in_progress',
    'In Progress',
    'The job is in progress'
  )

  public static readonly Completed = new GeckoJobStatus(
    'completed',
    'Completed',
    'The job is completed'
  )

  public static readonly Canceled = new GeckoJobStatus(
    'canceled',
    'Canceled',
    'The job is canceled'
  )

  //----------------------------------------------------------------------------

  /** A unique ID */
  public readonly id: string

  /** A user-facing name for this object, or its ID if none is populated */
  public readonly name: string

  /** A user-facing description for this specific publishing state */
  public readonly description: string

  // Private constructor ensures singleton pattern
  private constructor(id: string, name: string, description: string) {
    this.id = id
    this.name = name
    this.description = description
    allJobStatuses.set(id, this)
  }

  /**
   * A default sorting method for component data frame sources
   * @param a The first source object
   * @param b The second source object
   */
  public static DefaultSort(a: GeckoJobStatus, b: GeckoJobStatus): number {
    return defaultSortOrder.indexOf(a.id) - defaultSortOrder.indexOf(b.id)
  }

  /**
   * Returns the unique data source instance for a given ID if it exists
   * @param id The unique ID of this data source
   */
  public static GetByID(id: string | undefined): GeckoJobStatus | undefined {
    return allJobStatuses.get(id ?? '')
  }

  /**
   * Returns the unique data source instance for a given ID, or creates a
   * unique data source instance from scratch for this new ID
   * @param id The unique ID of this object
   */
  public static GetOrCreate(id: string): GeckoJobStatus {
    return allJobStatuses.get(id) ?? new GeckoJobStatus(id, id, '')
  }
}

// Define default sort order based on IDs of the static instances
const defaultSortOrder = [
  GeckoJobStatus.Scheduled.id,
  GeckoJobStatus.InProgress.id,
  GeckoJobStatus.Completed.id,
  GeckoJobStatus.Canceled.id,
]
